import { t, type Locale } from '@sd/i18n';
import { escapeHtml, num } from './format';

/**
 * The playtest account (#61, #85). A build for the server (`VITE_SERVER=1`)
 * sends players to sign in on the developer's site, shows the leaderboard
 * beside the lobby, and sends every finished match, then its survey; any
 * other build — `pnpm dev`, the screenshots — plays offline as before.
 */
export const serverMode = import.meta.env?.VITE_SERVER === '1';

export interface Account {
  readonly name: string;
  readonly admin: boolean;
}

export async function whoAmI(): Promise<Account | null> {
  const res = await fetch('/api/me', { credentials: 'same-origin' });
  return res.ok ? ((await res.json()) as Account) : null;
}

export interface MatchReport {
  readonly summary: {
    readonly seed: string;
    readonly players: number;
    readonly faction: string;
    readonly result: string;
    readonly winner: string | null;
    readonly won: boolean;
    readonly place: number;
    readonly minutes: number;
    readonly prestige: number;
  };
  readonly survey: Readonly<Record<string, string>>;
  readonly log: unknown;
}

/** Sends a finished match (result, Prestige, log); resolves to its id on the server, or null. */
export async function sendReport(report: MatchReport): Promise<string | null> {
  try {
    const res = await fetch('/api/matches', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(report),
    });
    return res.ok ? ((await res.json()) as { id: string }).id : null;
  } catch {
    return null;
  }
}

/** Attaches the survey to a match already sent. */
export async function sendSurvey(id: string, answers: Record<string, string>): Promise<boolean> {
  try {
    const res = await fetch(`/api/matches/${encodeURIComponent(id)}/survey`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(answers),
    });
    return res.ok;
  } catch {
    return false;
  }
}

/**
 * The developer's site that signs players in (#85): the parent domain of
 * the game's subdomain (space-dominance.korolev-games.ru → korolev-games.ru),
 * or this very server when the game runs on its own host.
 */
export function portalOrigin(where: Pick<Location, 'protocol' | 'hostname' | 'host'>): string {
  const labels = where.hostname.split('.');
  const bare = /^[\d.]+$/.test(where.hostname) || where.hostname === 'localhost';
  return !bare && labels.length >= 3
    ? `${where.protocol}//${labels.slice(1).join('.')}`
    : `${where.protocol}//${where.host}`;
}

/** Off to the developer's site to sign in, and back here afterwards. */
export function goSignIn() {
  const next = encodeURIComponent(location.href);
  location.assign(`${portalOrigin(location)}/login?next=${next}`);
}

export interface LeaderRow {
  readonly place: number;
  readonly name: string;
  readonly prestige: number;
  readonly matches: number;
  readonly wins: number;
}

export interface Leaderboard {
  readonly top: readonly LeaderRow[];
  readonly me: LeaderRow | null;
}

export async function fetchLeaderboard(): Promise<Leaderboard | null> {
  try {
    const res = await fetch('/api/leaderboard', { credentials: 'same-origin' });
    return res.ok ? ((await res.json()) as Leaderboard) : null;
  } catch {
    return null;
  }
}

/** The soft-PvP table beside the lobby (#85): Prestige added up over every match. */
export function leaderboardHtml(
  locale: Locale,
  board: Leaderboard | null,
  account: Account | null,
  portal: string,
): string {
  const row = (r: LeaderRow) =>
    `<tr class="${r.name === account?.name ? 'sd-row--you' : ''}"><td>${r.place}</td><td>${escapeHtml(r.name)}</td><td><b>${num(locale, r.prestige, 1)}</b></td><td class="sd-muted">${r.matches} · ${r.wins}</td></tr>`;
  const me = board?.me ?? null;
  const inTop = me !== null && (board?.top ?? []).some((r) => r.name === me.name);
  const rows = board?.top.length
    ? `${board.top.map(row).join('')}${me && !inTop ? `<tr class="sd-leaders__gap"><td colspan="4">…</td></tr>${row(me)}` : ''}`
    : `<tr><td colspan="4" class="sd-muted">${t(locale, 'leaders.empty')}</td></tr>`;
  return `<aside class="sd-panel sd-panel--strong sd-leaders" aria-labelledby="leaders-title">
    <div class="sd-section__head"><h2 id="leaders-title">${t(locale, 'leaders.title')}</h2></div>
    <p class="sd-label">${t(locale, 'leaders.note')}</p>
    <table class="sd-table"><thead><tr><th>#</th><th>${t(locale, 'leaders.player')}</th><th>${t(locale, 'leaders.prestige')}</th><th>${t(locale, 'leaders.played')}</th></tr></thead><tbody>${rows}</tbody></table>
    ${account ? `<div class="sd-leaders__foot"><span class="sd-label">${t(locale, 'leaders.signedIn', { name: escapeHtml(account.name) })}</span><a class="sd-btn" href="${escapeHtml(portal)}/logout">${t(locale, 'leaders.signOut')}</a></div>` : ''}
  </aside>`;
}

/** The survey under the end-of-match screen (#62); its answers go to the author with the log. */
export const SURVEY = [
  { id: 'again', kind: 'choice', options: ['yes', 'rather-yes', 'rather-no', 'no'] },
  { id: 'clarity', kind: 'choice', options: ['1', '2', '3', '4', '5'] },
  { id: 'pace', kind: 'choice', options: ['slow', 'fine', 'fast'] },
  { id: 'best', kind: 'text' },
  { id: 'confusing', kind: 'text' },
  { id: 'bugs', kind: 'text' },
] as const;

export function surveyHtml(locale: Locale): string {
  const questions = SURVEY.map((q) => {
    const label = `<span class="sd-label">${t(locale, `survey.${q.id}`)}</span>`;
    if (q.kind === 'text') {
      return `<label class="sd-field">${label}<textarea class="sd-input" name="${q.id}" rows="2" maxlength="2000"></textarea></label>`;
    }
    return `<fieldset class="sd-field sd-survey__choice">${label}<div class="sd-segmented">${q.options
      .map(
        (o) =>
          `<label class="sd-btn"><input type="radio" name="${q.id}" value="${o}" />${escapeHtml(t(locale, `survey.${q.id}.${o}`))}</label>`,
      )
      .join('')}</div></fieldset>`;
  }).join('');
  return `<form class="sd-survey" data-form="survey">
    <h3>${t(locale, 'survey.title')}</h3>
    ${questions}
    <div class="sd-buttons"><button class="sd-btn sd-btn--primary" type="submit">${t(locale, 'survey.send')}</button><span class="sd-label" data-bind="survey-status"></span></div>
  </form>`;
}
